import { z } from 'zod';
import { BIOME_SETS, TUTORIAL_GEAR_SET } from './biome-sets.js';
import { BIOME_IDS } from './biomes.js';
import { getItem } from './items.js';
import { biomeOfLevel } from './loot.js';
import { UPGRADE_MATERIAL_RARITIES, UPGRADE_MATERIALS, } from './upgrade-materials.js';
/**
 * The smith's table (ADR 0013). Data, not code (CLAUDE.md rule 4): the odds,
 * the bonus and the price of every step live here and nowhere else; the
 * rules (`rules/upgrade.ts`) only read them.
 *
 * Percentages are whole numbers so that a stat of 10 at +15 % is exactly
 * 11.5, and rounds the same way on every machine.
 */
export const MAX_UPGRADE_LEVEL = 10;
export const UpgradeStepSchema = z.object({
    /** The level this attempt reaches when it succeeds. */
    level: z.number().int().min(1).max(MAX_UPGRADE_LEVEL),
    /** Chance of success, in percent. The same at every attempt: no pity. */
    chance: z.number().int().min(1).max(100),
    /** Cumulated stat bonus once reached, in percent of the template's stats. */
    bonus: z.number().int().min(1),
    /** Which of the item's biome materials it takes, and how many. */
    material: z.enum(UPGRADE_MATERIAL_RARITIES),
    quantity: z.number().int().min(1),
});
const STEPS = [
    { level: 1, chance: 100, bonus: 15, material: 'common', quantity: 1 },
    { level: 2, chance: 90, bonus: 35, material: 'common', quantity: 1 },
    { level: 3, chance: 75, bonus: 75, material: 'common', quantity: 1 },
    { level: 4, chance: 55, bonus: 85, material: 'common', quantity: 2 },
    { level: 5, chance: 35, bonus: 95, material: 'common', quantity: 3 },
    { level: 6, chance: 20, bonus: 105, material: 'rare', quantity: 1 },
    { level: 7, chance: 12, bonus: 115, material: 'rare', quantity: 1 },
    { level: 8, chance: 6, bonus: 125, material: 'rare', quantity: 2 },
    { level: 9, chance: 3, bonus: 135, material: 'epic', quantity: 1 },
    { level: 10, chance: 1, bonus: 150, material: 'epic', quantity: 1 },
];
/** `UPGRADE_STEPS[n - 1]` is the attempt that reaches +n. */
export const UPGRADE_STEPS = Object.freeze(STEPS.map((step) => UpgradeStepSchema.parse(step)));
/**
 * The smith's regular price in gold (ADR 0016):
 * `baseCost × 1.35^level reached`, per the piece's biome. The first three
 * steps in the meadow and forest receive the discount below.
 */
export const UPGRADE_BASE_COST = Object.freeze({
    meadow: 9,
    forest: 15,
    swamp: 26,
    desert: 42,
    snow: 67,
});
export const UPGRADE_COST_GROWTH = 1.35;
/** Make the first upgrades affordable while learning the meadow and forest. */
export const EARLY_UPGRADE_DISCOUNT = {
    biomes: ['meadow', 'forest'],
    throughLevel: 3,
    multiplier: 0.75,
};
/** From this level on, the piece is bound to its character. */
export const BIND_AT_LEVEL = 4;
/** Successes from this level on are announced to every player. */
export const ANNOUNCE_FROM_LEVEL = 8;
/**
 * From this level an upgraded piece shines (ADR 0013, amended): stars twinkle
 * on it, a halo lights up behind its wearer, and its « +N » takes its colour.
 * Good gear should make a character flashy, visible from across the square.
 */
export const SHINE_FROM_LEVEL = 3;
/**
 * One colour per level from +3, climbing like the rarities and past them:
 * cyan, blue, violet, magenta, pink, red, fire, gold. No white nor silver (a
 * star has to show on a steel or frost piece) and no green (the halo has to
 * show on the meadow's grass). The halo, the stars and the « +N » all take
 * the colour of their level.
 */
export const UPGRADE_LEVEL_COLORS = Object.freeze({
    3: 0x3fd0e0,
    4: 0x3a6fe0,
    5: 0x9b5cff,
    6: 0xe04cd8,
    7: 0xff7ac8,
    8: 0xff3b3b,
    9: 0xf07a1c,
    10: 0xffc82e,
});
/** At the top, the golden ring generated once (`effect.upgrade-glow`) turns over the halo. */
export const CROWN_AT_LEVEL = 10;
/** The monster that stands at the anvil of every village (`data/mobs.ts`). */
export const SMITH_MOB_ID = 'smith';
/** How close to a smith a player must stand, in tiles, diagonals included. */
export const SMITH_REACH = 2;
const SET_BIOME = new Map([...BIOME_SETS, TUTORIAL_GEAR_SET].flatMap((set) => Object.values(set.pieces).map(({ id }) => [id, set.biome])));
/**
 * The biome a piece comes from, whose materials the smith asks for. A set
 * piece belongs to its set's biome, whatever its rarity; a hand-made one to
 * the band of its required level (the starting kit is the meadow's, the
 * dragon set, level 10, the swamp's).
 */
export function originBiome(template) {
    const baseId = template.id.replace(/-(rare|epic|legendary)$/, '');
    return SET_BIOME.get(baseId) ?? biomeOfLevel(template.levelRequired);
}
/** Template id of a biome's material of this rarity. */
export function upgradeMaterialOf(biome, rarity) {
    return UPGRADE_MATERIALS[biome][rarity].id;
}
// The table is read on every attempt: a broken one must fail the server at
// boot, not a player at the anvil.
for (const [index, step] of UPGRADE_STEPS.entries()) {
    if (step.level !== index + 1)
        throw new Error(`Upgrade step ${String(index)} is not +${String(index + 1)}`);
}
for (const biome of BIOME_IDS) {
    for (const rarity of UPGRADE_MATERIAL_RARITIES) {
        const id = upgradeMaterialOf(biome, rarity);
        const material = getItem(id);
        if (material?.kind !== 'material' || material.rarity !== rarity || material.maxStack < 2) {
            throw new Error(`Upgrade material ${id} of ${biome} must be a stackable ${rarity} material`);
        }
    }
}
//# sourceMappingURL=upgrades.js.map